import React from 'react';
import { t } from '@/locales/strings';
import { reportError } from '@/utils/errorReporter';

const styles = {
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg, #F2F0F5)',
    padding:
      'max(24px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom))',
    color: 'var(--text, #2D1A1F)',
    zIndex: 9999,
    overflow: 'hidden',
  },
  halo: {
    position: 'absolute',
    top: '42%',
    left: '50%',
    width: 360,
    height: 360,
    marginLeft: -180,
    marginTop: -200,
    borderRadius: '50%',
    background:
      'radial-gradient(closest-side, var(--primary-pale, #F5D6E0), transparent 70%)',
    filter: 'blur(6px)',
    pointerEvents: 'none',
    animation: 'motionHaloBreathe 4000ms cubic-bezier(0.45,0,0.55,1) infinite',
  },
  stage: {
    position: 'relative',
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 14,
    padding: '36px 28px',
    animation: 'motionRise 640ms cubic-bezier(0.22,1,0.36,1) both',
  },
  logo: {
    width: 96,
    height: 'auto',
    display: 'block',
    marginBottom: 6,
  },
  title: {
    margin: '4px 0 0',
    fontFamily: "'Playfair Display', Georgia, serif",
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: 34,
    lineHeight: 1.15,
    letterSpacing: '-0.015em',
    color: 'var(--dark-brown, #2D1A1F)',
    fontVariationSettings: '"opsz" 96',
  },
  message: {
    margin: '2px 0 14px',
    maxWidth: 320,
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: 18,
    lineHeight: 1.7,
    color: 'var(--warm-brown, #6B4E56)',
  },
  cta: {
    appearance: 'none',
    border: 'none',
    marginTop: 4,
    minHeight: 52,
    padding: '14px 28px',
    background: 'var(--primary, #A8003F)',
    color: '#ffffff',
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    borderRadius: 999,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    boxShadow:
      '0 10px 24px -8px rgba(168, 0, 63, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
    WebkitTapHighlightColor: 'transparent',
    transition: 'transform 120ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 200ms ease',
  },
  ctaArrow: {
    fontSize: 15,
    display: 'inline-block',
    transform: 'translateY(-0.5px)',
  },
};

/**
 * ErrorBoundary — catches render errors in the tree below it so a
 * crash in one screen doesn't blank out the whole app. Renders a
 * humane editorial fallback: brand monogram, pause-the-moment copy,
 * then a single "begin again" CTA that reloads.
 *
 * Class-component-only so it can use componentDidCatch /
 * getDerivedStateFromError. Errors are logged to the browser console
 * with the full component stack. If a remote reporting service is
 * wired up later, this is the place.
 *
 * Styles are inline so the fallback is self-contained even if the
 * CSS module itself crashed. Brand fonts (Fraunces / Manrope) are
 * referenced by name so the system font stack fills in gracefully.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface the full stack so developers still see it in the field.
    console.error('[ErrorBoundary]', error, info?.componentStack);
    reportError(error, { componentStack: info?.componentStack, scope: 'ErrorBoundary' });
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* non-browser env */ }
  };

  render() {
    if (!this.state.error) return this.props.children;

    // Read language off <html lang> so the fallback still localises
    // even if AppContext itself crashed. Default to English.
    const lang =
      (typeof document !== 'undefined' &&
        document.documentElement.getAttribute('lang') === 'pt') ? 'pt' : 'en';

    return (
      <div role="alert" aria-live="assertive" style={styles.root}>
        {/* Ambient halo — same pale-primary bloom used on LoadingScreen
            so the fallback still feels like the same product. */}
        <span aria-hidden="true" style={styles.halo} />

        <div style={styles.stage}>
          {/* Brand logo — reassures the user they're still inside
              Be Bold, not on a browser error page. */}
          <img
            src="/logo.png"
            alt="Be Bold"
            width="96"
            height="96"
            style={styles.logo}
          />

          <h1 style={styles.title}>{t('error_boundary_title', lang)}</h1>
          <p style={styles.message}>{t('error_boundary_message', lang)}</p>

          <button type="button" style={styles.cta} onClick={this.handleReload}>
            {t('error_boundary_reload', lang)}
            <span aria-hidden="true" style={styles.ctaArrow}>{'→'}</span>
          </button>
        </div>
      </div>
    );
  }
}

